/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useContext, useState } from "react";
import Page from "components/common_components/Page";
import FormsContext from "./FormsContext";
import { useHistory } from "react-router-dom";
import Button from "components/common_components/Button";
import { hasElements } from "utils/arrays";
import { useDispatch } from "react-redux";
import { notifyError, notifySuccess } from "redux/actions/Notifications";
import Icon from "components/common_components/Icon";
import Status from "components/common_components/Status";
import PdfForm from "./components/PdfForm";
import AttorneySelect from "./components/AttorneySelect";
import useFormPreloaders from "./hooks/useFormPreloaders";
import FileCaseSelect from "./components/FileCaseSelect";
import { toAddendumsPayload } from "./utils";

const AddForm = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const {
        FormClients,
        InvoicingIssuers,
        addFormState: { category, form, client, fileCase, attorneyUser },
        setAddFormState,
        getAttorneyUsersOptions,
        getFileUrlAsync,
        pdfUrl,
        Forms,
    } = useContext(FormsContext);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // The forms list comes without field mappings: they are loaded for the selected form only
    const [formMappings, setFormMappings] = useState(null);
    const [addendums, setAddendums] = useState([]);
    const {
        clientValues,
        fieldTriggers,
        loadClientPreloader,
        setAttorneyPreloader,
        loadDocIssuerPreloader,
        initFieldTriggers,
        preloadData,
    } = useFormPreloaders();

    useEffect(() => {
        if (!(form && client)) {
            history.push("/forms/add/wizard");
        } else {
            if (client) {
                loadClientPreloader(client.id, FormClients);
            }
            if (form) {
                getFileUrlAsync(form);
                setFormMappings(null);
                Forms.getOneById({
                    id: form.id,
                    onSuccess: (fullForm) => {
                        setFormMappings(fullForm.formFieldMappings || []);
                        initFieldTriggers(fullForm.formFieldMappings);
                    },
                    onError: (error) => dispatch(notifyError(error.message || error)),
                });
            }
            loadDocIssuerPreloader(InvoicingIssuers);
        }
    }, [form, client]);

    const submitForm = (event, formValues) => {
        event.preventDefault();
        if (!formMappings) return;
        const mappingsWithValues = Object.keys(formValues).map((key) => {
            const value = formValues[key];
            const id = formMappings.find((x) => x.pdfField === key)?.id;
            return {
                id,
                value,
            };
        });
        if (form && client && hasElements(mappingsWithValues)) {
            setIsSubmitting(true);

            const payload = {
                clientId: client.id,
                formId: form.id,
                status: "open",
                attorneyUserId: attorneyUser?.id || null,
                module: fileCase?.module || null,
                fileCaseId: fileCase?.fileCaseId || null,
                fileNo: fileCase?.fileNo || null,
                values: mappingsWithValues.reduce((arr, mapping) => {
                    const valueObj = {
                        formClientId: null,
                        fieldMappingsId: mapping.id,
                        value: mapping.value,
                    };
                    return [...arr, valueObj];
                }, []),
                addendums: toAddendumsPayload(addendums),
            };

            FormClients.add({
                payload,
                onSuccess,
                onError,
            });
        }
    };

    const onSuccess = (newId) => {
        dispatch(notifySuccess("Form created successfully"));
        setIsSubmitting(false);
        history.push(`/forms/${newId}`);
    };

    const onError = (error) => {
        dispatch(notifyError(error.message || error));
        setIsSubmitting(false);
    };

    const onPreloadData = () => preloadData(formMappings);

    const onCancelHandler = () => history.push(`/forms`);

    return (
        <Page className="add-form-page">
            <div className="form-head">
                <div className="item form-status">
                    <Status id="new" />
                </div>
                <div className="item">{category?.category}</div>
                <div className="item">
                    <span>{form?.code}</span>
                    <span>({form?.version})</span>
                    {form?.isCurrent ? <Status id="active" /> : <Status id="oldVersion" />}
                </div>
                <div className="item">{client?.fullName}</div>
                <div className="item form-actions">
                    <Button secondary small onClick={onPreloadData} disabled={!formMappings}>
                        <Icon name="client-info" size="sm" color="white" />
                        <span>Preload data</span>
                    </Button>

                    <Button
                        secondary
                        small
                        type="submit"
                        loading={isSubmitting || !formMappings}
                        disabled={!formMappings}
                        formId="pdf-overlay-form"
                    >
                        <Icon name="save" size="sm" color="white" />
                        <span>Save</span>
                    </Button>

                    <Button secondary outline small className="cancel" onClick={onCancelHandler}>
                        Cancel
                    </Button>
                </div>
            </div>
            <div className="form-head">
                <AttorneySelect
                    value={attorneyUser || null}
                    options={getAttorneyUsersOptions()}
                    onChange={(user) => {
                        setAttorneyPreloader(user?.userAttorney);
                        setAddFormState((prev) => ({ ...prev, attorneyUser: user }));
                    }}
                />
                <FileCaseSelect
                    clientId={client?.id}
                    value={fileCase}
                    onChange={(fileCase) => setAddFormState((prev) => ({ ...prev, fileCase }))}
                />
            </div>
            <PdfForm
                pdfUrl={pdfUrl}
                clientValues={clientValues}
                onSubmit={submitForm}
                fieldTriggers={fieldTriggers}
                fieldMappings={formMappings}
                addendumInfo={{ formCode: form?.code }}
                addendums={addendums}
                onAddendumsChange={setAddendums}
            />
        </Page>
    );
};

export default AddForm;
